import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProfessionalDevelopment = () => {
  const developmentPrograms = [
    {
      id: 1,
      title: "Leadership Excellence Certification",
      duration: "6 months",
      participants: 45,
      completionRate: 92,
      image: "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?w=400&h=300&fit=crop",
      description: "Comprehensive leadership training covering strategic thinking, team management, and decision-making skills.",
      modules: [
        "Strategic Leadership & Vision Setting",
        "Team Building & Conflict Resolution",
        "Public Speaking & Communication",
        "Project Management & Execution"
      ],
      outcomes: [
        "87% received promotions within 12 months",
        "95% reported increased confidence",
        "78% took on additional leadership roles"
      ],
      nextCohort: "March 2025"
    },
    {
      id: 2,
      title: "Professional Skills Workshop Series",
      duration: "Ongoing",
      participants: 120,
      completionRate: 85,
      image: "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?w=400&h=300&fit=crop",
      description: "Monthly workshops covering essential professional skills from industry experts and successful alumni.",
      modules: [
        "Digital Marketing & Social Media",
        "Financial Planning & Investment",
        "Networking & Relationship Building",
        "Career Transition Strategies"
      ],
      outcomes: [
        "Average 25% salary increase",
        "Expanded professional networks",
        "Enhanced digital presence"
      ],
      nextCohort: "Every month"
    },
    {
      id: 3,
      title: "Mentorship Excellence Program",
      duration: "12 months",
      participants: 35,
      completionRate: 96,
      image: "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?w=400&h=300&fit=crop",
      description: "Intensive mentorship training to develop the next generation of Leo Circle leaders and community mentors.",
      modules: [
        "Mentoring Fundamentals",
        "Coaching Techniques",
        "Youth Development Psychology",
        "Program Design & Implementation"
      ],
      outcomes: [
        "100% became active mentors",
        "Mentored 150+ young professionals",
        "Created 8 new mentorship programs"
      ],
      nextCohort: "June 2025"
    }
  ];

  const careerAdvancement = [
    {
      category: "Promotions Achieved",
      stats: [
        { label: "Senior Management", count: 28, percentage: 18 },
        { label: "Team Lead Positions", count: 45, percentage: 30 },
        { label: "Department Heads", count: 15, percentage: 10 },
        { label: "C-Suite Executives", count: 8, percentage: 5 }
      ]
    },
    {
      category: "Career Transitions",
      stats: [
        { label: "Industry Switches", count: 32, percentage: 21 },
        { label: "Entrepreneurship", count: 18, percentage: 12 },
        { label: "International Opportunities", count: 25, percentage: 17 },
        { label: "Non-Profit Leadership", count: 12, percentage: 8 }
      ]
    }
  ];

  const certifications = [
    {
      name: "Project Management Professional (PMP)",
      holders: 23,
      icon: "CheckSquare",
      provider: "PMI"
    },
    {
      name: "Certified Public Accountant (CPA)",
      holders: 18,
      icon: "Calculator",
      provider: "AICPA"
    },
    {
      name: "Digital Marketing Certification",
      holders: 35,
      icon: "TrendingUp",
      provider: "Google/HubSpot"
    },
    {
      name: "Leadership in Energy and Environmental Design (LEED)",
      holders: 12,
      icon: "Leaf",
      provider: "USGBC"
    },
    {
      name: "Certified Information Systems Security Professional (CISSP)",
      holders: 15,
      icon: "Shield",
      provider: "ISC²"
    },
    {
      name: "Six Sigma Green Belt",
      holders: 20,
      icon: "Target",
      provider: "ASQ"
    }
  ];

  const testimonials = [
    {
      name: "Rajitha Seneviratne",
      position: "Senior Manager, Ernst & Young",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
      quote: "The leadership certification program transformed my approach to management. Within 8 months, I was promoted to Senior Manager and now lead a team of 15 professionals.",
      achievement: "Promoted twice in 18 months"
    },
    {
      name: "Malika Jayawardena",
      position: "Founder, EcoSolutions Lanka",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop&crop=face",
      quote: "Leo Circle's entrepreneurship workshops gave me the confidence to start my own sustainable technology company. We now employ 25 people and serve 100+ clients.",
      achievement: "Built LKR 50M revenue company"
    }
  ];

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Professional Development Excellence
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Investing in our members' growth through structured learning, mentorship, 
            and career advancement opportunities that create lasting impact.
          </p>
        </div>

        {/* Development Programs */}
        <div className="space-y-8 mb-16">
          {developmentPrograms.map((program) => (
            <div key={program.id} className="bg-card rounded-xl card-shadow overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                <div className="lg:col-span-1">
                  <div className="aspect-video lg:aspect-square overflow-hidden">
                    <Image
                      src={program.image}
                      alt={program.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                
                <div className="lg:col-span-2 p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-foreground mb-2">{program.title}</h3>
                      <p className="text-muted-foreground mb-4">{program.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-primary">{program.completionRate}%</div>
                      <div className="text-sm text-muted-foreground">Completion Rate</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <h4 className="font-bold text-foreground mb-3 flex items-center">
                        <Icon name="BookOpen" size={16} className="text-primary mr-2" />
                        Program Modules
                      </h4>
                      <ul className="space-y-2">
                        {program.modules.map((module, index) => (
                          <li key={index} className="text-sm text-muted-foreground flex items-start">
                            <Icon name="CheckCircle" size={14} className="text-accent mr-2 mt-0.5 flex-shrink-0" />
                            {module}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-bold text-foreground mb-3 flex items-center">
                        <Icon name="TrendingUp" size={16} className="text-primary mr-2" />
                        Key Outcomes
                      </h4>
                      <ul className="space-y-2">
                        {program.outcomes.map((outcome, index) => (
                          <li key={index} className="text-sm text-muted-foreground flex items-start">
                            <Icon name="Star" size={14} className="text-secondary mr-2 mt-0.5 flex-shrink-0" />
                            {outcome}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                      <div className="flex items-center">
                        <Icon name="Clock" size={16} className="mr-1" />
                        {program.duration}
                      </div>
                      <div className="flex items-center">
                        <Icon name="Users" size={16} className="mr-1" />
                        {program.participants} participants
                      </div>
                      <div className="flex items-center">
                        <Icon name="Calendar" size={16} className="mr-1" />
                        Next: {program.nextCohort}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Career Advancement Statistics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {careerAdvancement.map((category, categoryIndex) => (
            <div key={categoryIndex} className="bg-card rounded-xl p-8 card-shadow">
              <h3 className="text-xl font-bold text-foreground mb-6">{category.category}</h3>
              <div className="space-y-4">
                {category.stats.map((stat, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-foreground font-medium">{stat.label}</span>
                    <div className="flex items-center space-x-4">
                      <div className="w-24 bg-muted rounded-full h-2">
                        <div
                          className="h-2 rounded-full bg-primary"
                          style={{ width: `${stat.percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-bold text-primary w-8 text-right">{stat.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Professional Certifications */}
        <div className="bg-card rounded-xl p-8 card-shadow mb-16">
          <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center">
            <Icon name="Award" size={24} className="text-primary mr-3" />
            Professional Certifications Achieved
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert, index) => (
              <div key={index} className="flex items-center p-4 bg-surface rounded-lg">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name={cert.icon} size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-foreground text-sm">{cert.name}</h4>
                  <p className="text-xs text-muted-foreground mb-1">{cert.provider}</p>
                  <p className="text-xs text-primary font-medium">{cert.holders} members certified</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Success Testimonials */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-foreground mb-8 text-center">
            Career Transformation Stories
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-card rounded-xl p-8 card-shadow">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 overflow-hidden rounded-full mr-4">
                    <Image
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">{testimonial.name}</h4>
                    <p className="text-sm text-primary">{testimonial.position}</p>
                    <div className="bg-accent/10 text-accent px-2 py-1 rounded text-xs mt-1 inline-block">
                      {testimonial.achievement}
                    </div>
                  </div>
                </div>
                <blockquote className="text-muted-foreground italic">
                  "{testimonial.quote}"
                </blockquote>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-primary to-primary/80 rounded-xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Accelerate Your Professional Growth
            </h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Join a community that invests in your success. Access world-class training, 
              mentorship, and career advancement opportunities while serving your community.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-primary px-6 py-3 rounded-lg font-medium hover:bg-white/90 transition-smooth">
                Explore Programs
              </button>
              <button className="border border-white/30 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-smooth">
                Apply for Membership
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfessionalDevelopment;